import React, { useState, useRef, useEffect, useCallback } from 'react';
import TextareaAutosize from 'react-textarea-autosize';
import './CommandPalette.css';
import { CommandConfirmModal } from '../CommandConfirmModal/CommandConfirmModal';

interface CommandPaletteProps {
  // 将查询转换为命令
  onGenCommand: (query: string) => Promise<string>;
  // 执行命令
  onCommands: (commands: string[]) => void;
}

const COMMAND_PLACEHOLDER = "- 创建友方生物：创建盟友 惠惠 hp 50 mp 60 dex 6 ins 10 mig 6 wlp 10 物理防御加值1/固定防御12 魔防加值2\n- 创建敌方生物：创建敌人 [从Fultimator拷贝敌人JSON数据]\n- 战斗动作：依照顺序描述 [来源] [动作与MP消耗] [目标] [检定] [结果]";

export const CommandPalette: React.FC<CommandPaletteProps> = ({ onGenCommand, onCommands }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [command, setCommand] = useState('');
  const [isWaitingForGenCommand, setIsWaitingForGenCommand] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [commandToConfirm, setCommandToConfirm] = useState('');
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        if (!isExpanded && !showModal) {
          e.preventDefault();
          setIsExpanded(true);
          setCommand('');
          inputRef.current?.focus();
        }
      } else if (e.key === 'Escape') {
        setIsExpanded(false);
        setCommand('');
      }
    };

    // 点击外部时关闭面板（确认命令弹窗打开时不处理，避免弹窗内的点击被误判为"外部点击"而清空已输入内容）
    const handleClickOutside = (e: MouseEvent) => {
      if (isExpanded && !showModal && inputRef.current && !inputRef.current.contains(e.target as Node)) {
        setIsExpanded(false);
        setCommand('');
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      window.removeEventListener('keydown', handleKeyPress);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isExpanded, showModal]);

  const handleCommandChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    setCommand(newValue);

    // 开始输入时自动展开
    if (!isExpanded && newValue) {
      setIsExpanded(true);
    }
  };

  const handleGenCommand = async (query: string) => {
    if (query.trim()) {
      try {
        setIsWaitingForGenCommand(true);
        const command = await onGenCommand(query);
        setIsWaitingForGenCommand(false);
        setCommandToConfirm(command);
        setShowModal(true);
      } catch (e) {
        console.error(e);
        setIsWaitingForGenCommand(false);
      }
    }
  };

  const handleConfirm = (confirmedCommand: string) => {
    onCommands(confirmedCommand.split("\n").map(line => line.trim()).filter(line => line.length > 0));
    setShowModal(false);
    setIsExpanded(false);
    setCommand('');
  };

  // 从"确认命令"回退到"自然语言输入"，保留之前输入的自然语言内容以便快速修改重试
  // 弹窗的取消按钮已下线；Esc、点击遮罩层、点击右上角关闭按钮均复用这个行为
  const handleBack = () => {
    setShowModal(false);
    setIsExpanded(true);
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  // 初始展开时调整高度
  useEffect(() => {
    if (isExpanded) {
      inputRef.current?.focus();
    }
  }, [isExpanded]);

  return (
    <>
      <div
        className={`command-palette ${isExpanded ? 'expanded' : ''} ${isWaitingForGenCommand ? 'waiting' : ''}`}
        onClick={() => {
          if (!isExpanded) {
            setCommand('');
            setIsExpanded(true);
          }
        }}
      >
        <div className="command-input-container">
          <TextareaAutosize
            ref={inputRef}
            value={command}
            onChange={handleCommandChange}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                if (!isWaitingForGenCommand) {
                  handleGenCommand(command);
                }
              }
            }}
            placeholder={isExpanded ? "用自然语言描述战斗过程，按 Enter 确认\n" + COMMAND_PLACEHOLDER : "按 Enter 打开命令面板"}
            minRows={1}
            maxRows={10}
          />
        </div>
      </div>

      <CommandConfirmModal
        show={showModal}
        command={commandToConfirm}
        onConfirm={handleConfirm}
        onChange={setCommandToConfirm}
        onBack={handleBack}
      />
    </>
  );
};